import { useTicketList } from "@jugl-web/domain-resources/tickets/hooks/useTicketList";
import {
  CRMChannel,
  PlanTierIndex,
  parseEmailConvId,
} from "@jugl-web/rest-api";
import { CRMConversationStatus } from "@jugl-web/rest-api/integrations/types";
import { useSkipFutureAlerts } from "@jugl-web/domain-resources/common/hooks/useSkipFutureAlerts";
import {
  Avatar,
  InteractiveContainer,
  PlainButton,
  Tooltip,
} from "@jugl-web/ui-kit";
import {
  cx,
  useAppVariant,
  useResizeObserver,
  useTranslations,
} from "@jugl-web/utils";
import {
  CLOSE_CONVERSATION_ALERT_ID,
  REOPEN_CONVERSATION_ALERT_ID,
} from "@jugl-web/utils/storage";
import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/features/auth/authSlice";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getPlanTierIndex } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { hasVisitorIntelligenceData } from "@web-src/modules/contacts/ContactsPage/utils";
import { VisitorsIntelligenceDialog } from "@web-src/modules/contacts/ContactsPage/components/VisitorsIntelligenceDialog";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { ReactComponent as CloseConversation } from "./assets/close-conversation.svg";
import { ReactComponent as OpenConversation } from "./assets/open-conversation.svg";
import { ReactComponent as UnblockIcon } from "./assets/unblock.svg";
import { ReactComponent as OpenPanel } from "./assets/open-panel.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as InfoBlackIcon } from "./assets/info-black.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { ReactComponent as TimerIcon } from "./assets/timer.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { ReactComponent as TicketsIcon } from "./assets/tickets.svg";
import { formatWhatsappPhoneNumber } from "../../utils/formatWhatsappPhoneNumber";
import { getEmailConversationContactName } from "../../../../utils/getEmailConversationContactName";
import {
  ConversationStatusDialog,
  ConversationStatusDialogVariant,
} from "./components/ConversationStatusDialog";
import { ManageContactAlert } from "../../../ManageContactAlert";
import { useManageContact } from "../../../../hooks/useManageContact";
import { useSubscribeToTicketsMutations } from "../../../../hooks/useSubscribeToTicketsMutations";
import { useUpdateConversationStatus } from "../../../../hooks/useUpdateConversationStatus";
import { getConversationUsername } from "../../../WebChatAvatar/utils";
import { EmailAvatar } from "../../../EmailAvatar";
import { collapsedIconButtonClassName } from "../../../../consts";
import { ConversationSidePanelTab } from "../../../ConversationSidePanel/types";

const CHAT_TOP_BAR_COMPACT_WIDTH_PX = 560;

export const ChatTopBar: React.FC = () => {
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const { isAdmin } = useUserRole();
  const { currentPlan } = useSubscriptionV2();
  const { whatsappAgent } = useWhatsappAccount();
  const {
    selectedConversation,
    selectedChannel,
    isConversationSidePanelOpen,
    openConversationSidePanel,
    isContactBlocked,
    isChatExpired,
    chatSecondsLeft,
  } = useClientSupportPageContext();

  const [
    isVisitorsIntelligenceDialogOpen,
    setIsVisitorsIntelligenceDialogOpen,
  ] = useState(false);
  const [isConversationStatusDialogOpen, setIsConversationStatusDialogOpen] =
    useState(false);
  const [conversationStatusDialogVariant, setConversationStatusDialogVariant] =
    useState<ConversationStatusDialogVariant | undefined>();
  const [isManageContactAlertOpen, setIsManageContactAlertOpen] =
    useState(false);
  const { areFutureAlertsSkipped, onSkipFutureAlertsChange } =
    useSkipFutureAlerts();
  const { contactName: manageContactName, handleManageContact } =
    useManageContact();
  const { handleUpdateConversationStatus } = useUpdateConversationStatus();

  const isConversationOpen = useMemo(() => {
    if (!selectedConversation || !("conv_status" in selectedConversation)) {
      return true;
    }

    return selectedConversation.conv_status !== CRMConversationStatus.closed;
  }, [selectedConversation]);

  const isWhatsappChannel = selectedChannel === CRMChannel.whatsapp;
  const isWebChannel = selectedChannel === CRMChannel.chatbot;
  const isTwilioChannel = selectedChannel === CRMChannel.twilio;
  const isEmailChannel = selectedChannel === CRMChannel.email;
  const isReopenDisabled = !isConversationOpen && isChatExpired;

  const emailSenderName =
    isEmailChannel && selectedConversation
      ? getEmailConversationContactName(selectedConversation, {
          withEmailFallback: false,
        }) || undefined
      : undefined;

  const hasBusinessPlanOrAbove = useMemo(
    () =>
      currentPlan && getPlanTierIndex(currentPlan) >= PlanTierIndex.business,
    [currentPlan]
  );

  const hasVisitorIntelligence = useMemo(
    () => hasVisitorIntelligenceData(selectedConversation?.contact_info || {}),
    [selectedConversation]
  );

  const conversationName = useMemo(() => {
    if (!selectedConversation) return "";

    // for email the conversation name is the thread subject
    if (isEmailChannel)
      return (
        selectedConversation.name ||
        t({
          id: "client-support-page.email-no-subject",
          defaultMessage: "(No subject)",
        })
      );

    return selectedConversation.contact_info?.name || selectedConversation.name;
  }, [selectedConversation, isEmailChannel, t]);

  const conversationSubtitle = useMemo(() => {
    if (!selectedConversation) return null;

    if (selectedChannel === CRMChannel.whatsapp) {
      return formatWhatsappPhoneNumber(
        selectedConversation.conv_id,
        whatsappAgent?.settings.hide_num || false
      );
    }

    if (selectedChannel === CRMChannel.instagram) {
      return "user_name" in selectedConversation
        ? selectedConversation.user_name || null
        : null;
    }

    if (
      selectedChannel === CRMChannel.chatbot ||
      selectedChannel === CRMChannel.twilio
    ) {
      return `Chat ID: ${selectedConversation.conv_id}`;
    }

    if (selectedChannel === CRMChannel.email) {
      return [
        emailSenderName,
        `<${parseEmailConvId(selectedConversation.conv_id).email}>`,
      ]
        .filter(Boolean)
        .join(" ");
    }

    return null;
  }, [
    emailSenderName,
    selectedChannel,
    selectedConversation,
    whatsappAgent?.settings.hide_num,
  ]);

  const formattedTimer = `${String(
    Math.floor((chatSecondsLeft / 3600) % 24)
  ).padStart(2, "0")}:${String(
    Math.floor((chatSecondsLeft / 60) % 60)
  ).padStart(2, "0")}:${String(chatSecondsLeft % 60).padStart(2, "0")}`;

  const handleConversationActionClick = useCallback(() => {
    const variant: ConversationStatusDialogVariant = isConversationOpen
      ? "close"
      : "reopen";
    const alertId =
      variant === "close"
        ? CLOSE_CONVERSATION_ALERT_ID
        : REOPEN_CONVERSATION_ALERT_ID;

    if (areFutureAlertsSkipped(alertId)) {
      handleUpdateConversationStatus(variant);
      return;
    }

    setConversationStatusDialogVariant(variant);
    setIsConversationStatusDialogOpen(true);
  }, [
    areFutureAlertsSkipped,
    handleUpdateConversationStatus,
    isConversationOpen,
  ]);

  const handleConversationStatusConfirm = useCallback(
    ({
      skipFutureAlerts,
      variant,
    }: {
      skipFutureAlerts: boolean;
      variant: ConversationStatusDialogVariant;
    }) => {
      const alertId =
        variant === "close"
          ? CLOSE_CONVERSATION_ALERT_ID
          : REOPEN_CONVERSATION_ALERT_ID;

      if (skipFutureAlerts) {
        onSkipFutureAlertsChange(alertId, true);
      }

      handleUpdateConversationStatus(variant);
    },
    [handleUpdateConversationStatus, onSkipFutureAlertsChange]
  );

  const { entityId } = useEntitySelectedProvider();
  const meId = useSelector(selectUserId);

  const contactId = selectedConversation?.contact_info?.id || undefined;

  const { totalCount, hasMore, setTickets, setTotalCount } = useTicketList({
    entityId,
    contactId,
    filter: "all",
    meId: meId ?? "",
    skip: !contactId || !isCx,
    sort: "desc",
    pageSize: 1,
  });

  useSubscribeToTicketsMutations({
    sort: "desc",
    hasMore,
    filter: "all",
    meId: meId ?? "",
    contactId,
    setTickets,
    setTotalCount,
  });

  const [chatWindowWidth, setChatWindowWidth] = useState(0);

  const handleTopBarResize = useCallback((entry: ResizeObserverEntry) => {
    setChatWindowWidth(entry.contentRect.width);
  }, []);

  const { ref: topBarRef } = useResizeObserver<HTMLDivElement>({
    onResize: handleTopBarResize,
  });

  const isCompactTopBar =
    chatWindowWidth > 0 && chatWindowWidth <= CHAT_TOP_BAR_COMPACT_WIDTH_PX;

  const topbarActions = useMemo(
    () => [
      {
        id: "tickets",
        tooltip: t(
          {
            id: "client-support-page.customer-related-active-tickets",
            defaultMessage: "Customer related{br}active Tickets",
          },
          { br: <br /> }
        ),
        className: "px-2.5",
        content: (
          <>
            {isCompactTopBar ? (
              <TicketsIcon />
            ) : (
              <span>
                {t({
                  id: "client-support-page.tickets",
                  defaultMessage: "Tickets",
                })}
              </span>
            )}
            <div className="flex h-[18px] min-w-[21px] items-center justify-center rounded-2xl border border-solid border-[#EEF2F5] bg-white px-1 font-secondary text-[10px] font-medium text-[#5A6367]">
              {totalCount}
            </div>
          </>
        ),
        onClick: () =>
          openConversationSidePanel({
            variant: ConversationSidePanelTab.tickets,
          }),
        isHidden: !isCx,
      },
      {
        id: "conversation",
        tooltip: isConversationOpen
          ? t({
              id: "client-support-page.close-conversation",
              defaultMessage: "Close Conversation",
            })
          : isReopenDisabled
            ? t({
                id: "client-support-page.conversation-cant-be-reopened-expired",
                defaultMessage:
                  "Conversation cant be reopened as its expired. It will reopen automatically when the customer replies",
              })
            : t({
                id: "client-support-page.reopen-conversation",
                defaultMessage: "Reopen Conversation",
              }),
        className: isConversationOpen || isCompactTopBar ? "w-8" : "px-2.5",
        content: isConversationOpen ? (
          <CloseConversation />
        ) : (
          <>
            {!isCompactTopBar && (
              <span>
                {t({
                  id: "client-support-page.reopen",
                  defaultMessage: "Reopen",
                })}
              </span>
            )}
            <OpenConversation />
          </>
        ),
        onClick: isReopenDisabled ? undefined : handleConversationActionClick,
        disabled: isReopenDisabled,
        isHidden: (isWhatsappChannel && isContactBlocked) || !isCx,
      },
      {
        id: "unblock",
        tooltip: isCx
          ? t({
              id: "client-support-page.unblock-customer",
              defaultMessage: "Unblock Customer",
            })
          : t({
              id: "client-support-page.unblock-user",
              defaultMessage: "Unblock User",
            }),
        className: isCompactTopBar ? "w-8" : "px-2.5",
        content: (
          <>
            {!isCompactTopBar && (
              <span>
                {t({
                  id: "client-support-page.unblock",
                  defaultMessage: "Unblock",
                })}
              </span>
            )}
            <UnblockIcon />
          </>
        ),
        onClick: () => setIsManageContactAlertOpen(true),
        isHidden: !isWhatsappChannel || !isContactBlocked,
      },
      {
        id: "panel",
        tooltip: t({
          id: "client-support-page.open-side-panel",
          defaultMessage: "Open Side Panel",
        }),
        className: "w-8",
        content: <OpenPanel />,
        onClick: () =>
          openConversationSidePanel({
            variant: ConversationSidePanelTab.customer,
          }),
        isHidden: isConversationSidePanelOpen,
      },
    ],
    [
      handleConversationActionClick,
      isContactBlocked,
      isConversationOpen,
      isConversationSidePanelOpen,
      isCompactTopBar,
      isCx,
      isReopenDisabled,
      isWhatsappChannel,
      openConversationSidePanel,
      t,
      totalCount,
    ]
  );

  if (!selectedConversation) {
    return null;
  }
  return (
    <>
      <InteractiveContainer
        className="jugl__custom-scrollbar-horizontal w-full min-w-0 overflow-x-auto border-0 border-b border-solid border-dark-100"
        ref={topBarRef}
        onClick={() =>
          openConversationSidePanel({
            variant: ConversationSidePanelTab.customer,
          })
        }
      >
        <div className="flex h-[60px] w-max min-w-full items-center justify-between gap-3 px-8">
          <div className="flex shrink-0 items-center gap-3">
            {isTwilioChannel ? (
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                style={{
                  background:
                    "linear-gradient(180deg, #CECED3 0%, #ECEFF0 69.27%)",
                }}
              >
                <UserIcon />
              </div>
            ) : isEmailChannel ? (
              <EmailAvatar className="h-10 w-10" />
            ) : (
              <Avatar
                size="md"
                username={
                  isWebChannel
                    ? getConversationUsername(conversationName)
                    : conversationName
                }
                className="h-10 w-10 shrink-0"
              />
            )}
            <div className="flex shrink-0 flex-col gap-0.5 whitespace-nowrap">
              <span className="font-secondary text-sm leading-2 font-medium text-[#333333]">
                {conversationName}
              </span>
              <span className="font-secondary text-xs leading-[140%] text-[#828282]">
                {conversationSubtitle}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {chatSecondsLeft > 0 && !isContactBlocked && (
              <Tooltip
                className="text-center"
                renderTrigger={({ props, ref }) => (
                  <div
                    className="flex h-8 w-[100px] cursor-default items-center justify-center gap-1 rounded-lg bg-dark px-2.5 font-secondary text-[13px] font-medium text-white transition-colors hover:bg-black"
                    {...props}
                    ref={ref}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TimerIcon />
                    <span className="w-[60px] text-center">
                      {formattedTimer}
                    </span>
                  </div>
                )}
              >
                {t(
                  {
                    id: "client-support-page.time-left-to-respond",
                    defaultMessage:
                      "<highlightedText>Time left to respond ⌛</highlightedText>{br}After 24 hours from last Customer activity, conversation will automatically be marked as expired ℹ️",
                  },
                  {
                    br: <br />,
                    highlightedText: (text: ReactNode) => (
                      <span className="font-semibold">{text}</span>
                    ),
                  }
                )}
              </Tooltip>
            )}
            {isCx && isWebChannel && isAdmin && (
              <Tooltip
                className="text-center"
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    className={cx(
                      "group rounded-lg p-px",
                      !hasBusinessPlanOrAbove || hasVisitorIntelligence
                        ? "bg-linear-to-r from-[#A9BDFF] to-[#F586FF]"
                        : "bg-grey-300"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (!hasBusinessPlanOrAbove) {
                        setIsVisitorsIntelligenceDialogOpen(true);
                        return;
                      }
                      openConversationSidePanel({
                        variant: ConversationSidePanelTab.customer,
                      });
                    }}
                    {...props}
                    ref={ref}
                  >
                    <div
                      className={cx(
                        "flex h-[30px] items-center justify-center gap-1.5 rounded-[7px] bg-white font-secondary text-sm font-medium text-[#1A2023] transition-all",
                        isCompactTopBar ? "w-8" : "px-2.5",
                        !hasBusinessPlanOrAbove || hasVisitorIntelligence
                          ? "from-[#FBF8FF] to-[#F1F8FF] group-hover:bg-linear-to-r"
                          : "ring-grey-300 ring-inset hover:ring-1"
                      )}
                    >
                      {!isCompactTopBar && (
                        <span>
                          {!hasBusinessPlanOrAbove
                            ? t({
                                id: "client-support-page.get-visitor-info",
                                defaultMessage: "Get Visitor Info",
                              })
                            : t({
                                id: "client-support-page.visitor-info",
                                defaultMessage: "Visitor Info",
                              })}
                        </span>
                      )}
                      {!hasBusinessPlanOrAbove ? (
                        <PricingIcon />
                      ) : hasVisitorIntelligence ? (
                        <InfoIcon />
                      ) : (
                        <InfoBlackIcon />
                      )}
                    </div>
                  </PlainButton>
                )}
              >
                {!hasBusinessPlanOrAbove
                  ? t({
                      id: "client-support-page.get-visitor-intelligence",
                      defaultMessage:
                        "Get Visitor Intelligence to know your Users better ✨",
                    })
                  : hasVisitorIntelligence
                    ? t({
                        id: "client-support-page.visitor-intelligence-captured",
                        defaultMessage:
                          "Visitor Intelligence info was captured 💫 Check it out!",
                      })
                    : t({
                        id: "client-support-page.visitor-intelligence-not-found",
                        defaultMessage:
                          "Visitor Intelligence info was not found for this User 🔍",
                      })}
              </Tooltip>
            )}
            {topbarActions
              .filter((action) => !action.isHidden)
              .map(({ id, tooltip, className, content, onClick, disabled }) => (
                <Tooltip
                  key={id}
                  className="text-center"
                  renderTrigger={({ props, ref }) => (
                    <PlainButton
                      className={cx(
                        collapsedIconButtonClassName,
                        "flex h-8 items-center justify-center gap-1.5 font-secondary text-sm font-medium text-[#1A2023]",
                        disabled && "opacity-40",
                        className
                      )}
                      {...props}
                      ref={ref}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick?.();
                      }}
                      disabled={disabled}
                    >
                      {content}
                    </PlainButton>
                  )}
                >
                  {tooltip}
                </Tooltip>
              ))}
          </div>
        </div>
      </InteractiveContainer>
      <VisitorsIntelligenceDialog
        isOpen={isVisitorsIntelligenceDialogOpen}
        onRequestClose={() => setIsVisitorsIntelligenceDialogOpen(false)}
      />
      <ConversationStatusDialog
        variant={conversationStatusDialogVariant}
        conversationName={conversationName}
        isOpen={isConversationStatusDialogOpen}
        onRequestClose={() => {
          setIsConversationStatusDialogOpen(false);
          setConversationStatusDialogVariant(undefined);
        }}
        onConfirm={handleConversationStatusConfirm}
      />
      <ManageContactAlert
        variant="unblock"
        contactName={manageContactName}
        isOpen={isManageContactAlertOpen}
        onConfirm={handleManageContact}
        onRequestClose={() => setIsManageContactAlertOpen(false)}
      />
    </>
  );
};
